import { Globe } from 'lucide-react';
import { useState } from 'react';
import { getCountryFlagUrl, cn } from '../../lib/utils';

interface CountryFlagProps {
  /** ISO-3166-1 alpha-2 country code (e.g. "ID", "US") */
  iso2: string | null | undefined;
  /** Width in px. Default 20. */
  size?: number;
  /** Whether to show the country code text after the flag */
  showCode?: boolean;
  className?: string;
}

/**
 * Renders a country flag as a PNG image (via flagcdn.com).
 * Falls back to a globe icon + code text for unknown/missing codes or load failures.
 *
 * Why PNG instead of emoji: Windows browsers don't render regional-indicator pairs
 * as flag glyphs — they appear as letters (e.g., "ID"). PNG works uniformly.
 */
export function CountryFlag({ iso2, size = 20, showCode, className }: CountryFlagProps) {
  const [errored, setErrored] = useState(false);
  const url = getCountryFlagUrl(iso2, size * 2); // 2x for HiDPI
  const code = iso2 ? iso2.toUpperCase() : '';

  if (!url || errored) {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <Globe size={Math.round(size * 0.85)} className="text-text-tertiary" />
        {showCode && (
          <span className="font-mono text-xs text-text-tertiary">{code || '??'}</span>
        )}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <img
        src={url}
        /* showCode already displays the code as text; empty alt + no title
           keeps screen readers from reading the code twice ("US US"). */
        alt={showCode ? '' : code}
        width={size}
        height={Math.round(size * 0.75)}
        className="rounded-sm border border-border-subtle/50 shadow-sm shrink-0 object-cover"
        loading="lazy"
        onError={() => setErrored(true)}
      />
      {showCode && <span className="font-mono text-xs">{code}</span>}
    </span>
  );
}
